/**
 * RCWebPhone — In-browser WebRTC softphone for RingCentral.
 *
 * Replaces the legacy iframe-based RCSoftphone. Uses RingCentral's official
 * `ringcentral-web-phone` SDK to:
 *   • register a SIP client over WebSocket (provisioned server-side via
 *     /api/integrations/ringcentral/webphone/provision)
 *   • make outbound calls with real two-way audio in the browser tab
 *   • receive inbound calls with an Accept/Decline popup
 *   • mute, hold, send DTMF, hang up
 *
 * Audio playback is handled automatically by the SDK — it creates one
 * <audio> element per call session and we attach it to our hidden mount.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import WebPhone from "ringcentral-web-phone";
import { toast } from "sonner";
import {
  Phone, PhoneOff, PhoneIncoming, Mic, MicOff, Pause, Play,
  Hash, X, Loader2, Wifi, WifiOff, CheckCircle2, AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const DIALPAD = [
  ["1", ""], ["2", "ABC"], ["3", "DEF"],
  ["4", "GHI"], ["5", "JKL"], ["6", "MNO"],
  ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
  ["*", ""], ["0", "+"], ["#", ""],
];

const fmtDuration = (sec) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export default function RCWebPhone({ prefillNumber, activeLead }) {
  const [regState, setRegState] = useState("disconnected"); // disconnected | provisioning | registering | registered | failed
  const [regError, setRegError] = useState(null);
  const [dialNumber, setDialNumber] = useState("");
  const [session, setSession] = useState(null); // active CallSession
  const [callState, setCallState] = useState("idle"); // idle | dialing | ringing | connected | ending
  const [inbound, setInbound] = useState(null); // pending inbound session
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [showDtmf, setShowDtmf] = useState(false);
  const [callerInfo, setCallerInfo] = useState({ number: "", name: "" });
  const [elapsedSec, setElapsedSec] = useState(0);
  const [callerId, setCallerId] = useState(null);

  const wpRef = useRef(null);
  const audioMountRef = useRef(null);
  const callStartRef = useRef(null);
  // Iter 193 (Tatiana #a85a8d0d) — track active-call state in a ref so
  // the once-mounted `inboundCall` event handler can decide whether to
  // auto-decline an interrupting inbound call. Without this ref the
  // handler captures `isInCall` from the initial render (always false)
  // and lets the new ring tone + popup blow up the active conversation.
  const isInCallRef = useRef(false);
  // Iter 140.5 — Stephanie issue 0394a122: track the moment the call
  // SESSION was created (always set, vs `callStartRef` which only sets
  // when an SDK "answered"/"accepted"/"confirmed" event actually
  // fires). When the SDK doesn't surface an "answered" event (RC SIP
  // quirk when picked up by a desk-phone receptionist on a multi-line
  // setup), but the session lived for > 12 seconds, we treat that as
  // a real conversation and override the outcome from no-answer →
  // connected. This rescues legitimate connected calls from being
  // mislabelled.
  const sessionStartRef = useRef(null);
  const lastSessionRef = useRef(null);
  const timerRef = useRef(null);
  const [micState, setMicState] = useState("unknown"); // unknown | requesting | granted | denied | unsupported

  // Probe mic permission as early as possible so the user is prompted on mount,
  // not at the moment they click "Call". When the app is loaded inside an
  // iframe whose `allow="microphone"` policy is missing, this surfaces it.
  const requestMic = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setMicState("unsupported");
      return false;
    }
    try {
      setMicState("requesting");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Release the probe stream immediately — the SDK will request its own.
      stream.getTracks().forEach((t) => t.stop());
      setMicState("granted");
      return true;
    } catch (e) {
      console.warn("mic permission denied", e);
      setMicState("denied");
      return false;
    }
  }, []);

  // Apply caller hint when SalesDialer/CommsDashboard prefills a number.
  useEffect(() => {
    if (prefillNumber && !session) setDialNumber(prefillNumber);
  }, [prefillNumber, session]);

  // ── Provision + register on mount ──
  // Tracks whether the failure is because the rep hasn't connected RC OAuth
  // (a soft, friendly state) vs an actual provisioning error (loud red banner).
  const [oauthRequired, setOauthRequired] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let webPhone = null;
    // Iter 271 (Jul 2026) — track whether `webPhone.start()` finished so
    // the cleanup path can decide whether it's safe to `dispose()`.
    // Calling `dispose()` while the SIP WebSocket is still in the
    // CONNECTING state throws `InvalidStateError: Failed to execute
    // 'send' on 'WebSocket'` because the SDK's dispose chain
    // (unregister → register → send) tries to write a REGISTER frame to
    // a socket that never finished the handshake. Common triggers:
    //   • Rep navigates away from Dialer/Comms while the softphone is
    //     still provisioning (~1-3s window on cold OAuth).
    //   • React StrictMode double-mount in dev (harmless, but noisy).
    //   • Flaky network / expired OAuth causing WS handshake to hang.
    let started = false;

    const init = async () => {
      try {
        // Surface mic permission state first so it's visible before SIP register.
        // Don't BLOCK on it though — register can succeed without mic, the user
        // just won't be able to place a call until they grant access.
        await requestMic();

        setRegState("provisioning");
        const { data } = await axios.get(`${API}/integrations/ringcentral/webphone/provision`, { headers: authHeaders() });
        if (cancelled) return;
        setCallerId(data.callerId || null);

        setRegState("registering");
        webPhone = new WebPhone({ sipInfo: data.sipInfo, debug: false });
        wpRef.current = webPhone;

        // ── Inbound calls ──
        webPhone.on("inboundCall", (s) => {
          // Iter 193 (Tatiana #a85a8d0d, #9c08c1e8) — if the rep is
          // ALREADY on a call when a new inbound rings, do NOT show
          // the popup or play the ringtone. The previous behavior
          // interrupted Tatiana's active conversation with a spam
          // call's ringer and audio focus stole away her mic. Auto-
          // decline immediately and toast a non-intrusive notice so
          // the rep knows what happened.
          //
          // Also auto-decline when RingCentral flags the call as
          // spam — most SIP providers expose this via session
          // `remoteIdentity` / `headers["X-Spam"]`; we check a few
          // common spots and fall through if we can't tell.
          const callerNumber = s?.remoteNumber || s?.remoteIdentity || "";
          const callerName = s?.remotePeer || s?.remoteName || "";
          const isSpam = (() => {
            try {
              const blob = JSON.stringify({
                hdr: s?.headers || s?.sipHeaders || {},
                rid: s?.remoteIdentity || "",
                name: callerName,
              }).toLowerCase();
              return blob.includes("spam") || blob.includes("scam") || blob.includes("robocall");
            } catch { return false; }
          })();

          if (isInCallRef.current) {
            // Active call in progress — auto-decline silently.
            try { s.decline?.(); } catch { /* SDK may not expose decline */ }
            try {
              const lbl = callerName || callerNumber || "Unknown";
              // Sonner is imported at the top of this file as `toast`.
              toast.info(`Incoming call from ${lbl} auto-declined — you're on a call`, { duration: 4500 });
            } catch { /* toast optional */ }
            return;
          }

          if (isSpam) {
            // Tagged as spam by the carrier — don't ring the rep.
            try { s.decline?.(); } catch { /* SDK may not expose decline */ }
            try {
              toast.info(`Spam call from ${callerNumber || "unknown"} auto-declined`, { duration: 3500 });
            } catch { /* toast optional */ }
            return;
          }

          setInbound(s);
          setCallerInfo({
            number: callerNumber,
            name: callerName,
          });
          s.once("disposed", () => setInbound((cur) => (cur === s ? null : cur)));
          // Play a soft notification (browser-only)
          try { new Audio("/sounds/ring.mp3").play().catch(() => {}); } catch {}
        });

        // ── Outbound call lifecycle handled per-session in `placeCall` ──

        await webPhone.start();
        started = true;
        if (cancelled) {
          started = false;
          try { await webPhone.dispose(); } catch { /* WS may have closed already */ }
          return;
        }
        setRegState("registered");
        setRegError(null);
        setOauthRequired(false);
      } catch (err) {
        // Detect "rep hasn't connected RC OAuth" → render the friendly CTA
        // instead of a noisy console error + red banner. The 403 is expected
        // per the Bundle 2 fallback policy (Block, don't fall back to JWT).
        const detail = err?.response?.data?.detail || "";
        const isOauthRequired = err?.response?.status === 403 &&
          typeof detail === "string" && detail.includes("rc_oauth_required");
        if (!cancelled) {
          if (isOauthRequired) {
            setRegState("failed");
            setOauthRequired(true);
            setRegError(null);
          } else {
            console.error("WebPhone register failed", err);
            setRegState("failed");
            setRegError(detail || err.message || "Registration failed");
          }
          // Iter 195 Slice B — audit every non-OAuth provisioning
          // failure so managers can see "green button did nothing"
          // reports without correlating console logs.
          if (!isOauthRequired) {
            try {
              await axios.post(
                `${API}/integrations/ringcentral/webphone/provision-failure`,
                {
                  phase:       regState === "provisioning" ? "provision" : "register",
                  http_status: err?.response?.status || null,
                  error:       (detail || err?.message || "unknown")?.slice(0, 600),
                  user_agent:  typeof navigator !== "undefined" ? (navigator.userAgent || "") : "",
                },
                { headers: authHeaders() },
              );
            } catch { /* audit is best-effort */ }
          }
        }
      }
    };
    init();
    return () => {
      cancelled = true;
      const wp = wpRef.current;
      wpRef.current = null;
      if (timerRef.current) clearInterval(timerRef.current);
      // Only dispose if `start()` completed — otherwise the SIP
      // WebSocket is still in CONNECTING state and the SDK's
      // dispose chain (unregister → register → ws.send) will throw
      // `InvalidStateError`. Best-effort raw-socket close instead.
      if (wp && started) {
        try { wp.dispose(); } catch { /* WS may have closed already */ }
      } else if (wp) {
        // Never registered — close the underlying WebSocket directly
        // if we can reach it, so we don't leak a half-open connection.
        try {
          const ws = wp?.sipClient?.ws || wp?.sipClient?._ws || wp?.client?.ws;
          if (ws && typeof ws.close === "function" && ws.readyState !== 3) {
            ws.close();
          }
        } catch { /* SDK internals may vary across versions */ }
      }
    };
  }, []);

  // ── Mount the SDK-managed audio element so the user actually hears it ──
  // Iter 177.4 (Himagna #f6ed1dab Feb 2026) — "cannot hear the calls going
  // out". Most reports of "no outgoing audio" on a WebRTC softphone come
  // down to ONE of three things, and the previous code only handled the
  // first by accident:
  //   (1) The audio element wasn't mounted in the DOM. (Handled — we
  //       append it to `audioMountRef`.)
  //   (2) Browser autoplay policy refused to start the element silently.
  //       Chrome blocks `<audio autoplay>` until the page has had a user
  //       gesture in the active sticky-activation window. We now log a
  //       clear console warning if `.play()` returns a rejected promise
  //       so reps see "[webphone] autoplay blocked" in DevTools instead
  //       of pure silence — they can click anywhere in the page to
  //       unblock and the next call will play.
  //   (3) Volume defaulted to 0 / muted=true. We explicitly set
  //       volume=1.0 and muted=false on the element so the SDK can't
  //       leave it inaudibly configured.
  // We also re-run on `callState === "connected"` so that the play()
  // nudge happens at the moment the rep is most likely to have a fresh
  // user gesture queued.
  useEffect(() => {
    if (!session || !audioMountRef.current) return undefined;

    // Iter 192f (Joshua, June 23 — "no dial tone"): the SDK sometimes
    // populates `session.audioElement` a tick or two AFTER the session
    // object itself is set on React state (it waits for ICE / SRTP
    // negotiation). Because the React effect dependency is just
    // `[session, callState]` and the session OBJECT reference never
    // changes when `.audioElement` is later assigned, the original mount
    // attempt early-returned and never re-ran — rep heard silence
    // through the entire dialing + ringing phase. Now: try to mount
    // immediately; if the element isn't ready yet, poll for up to 3s
    // until it is. Stops on success or on session teardown.
    const tryMount = () => {
      const el = session?.audioElement;
      if (!el || !audioMountRef.current) return false;
      // Idempotent — if it's already mounted (same element under our
      // mount node) we don't need to redo anything.
      if (el.parentElement === audioMountRef.current) {
        try { if (el.paused) el.play()?.catch(() => {}); } catch { /* noop */ }
        return true;
      }
      audioMountRef.current.innerHTML = "";
      el.autoplay = true;
      el.muted = false;
      try { el.volume = 1.0; } catch { /* some sandboxed contexts disallow */ }
      el.setAttribute("data-testid", "webphone-audio");
      audioMountRef.current.appendChild(el);
      const p = typeof el.play === "function" ? el.play() : null;
      if (p && typeof p.catch === "function") {
        p.catch((err) => {
          console.warn(
            "[webphone] audio.play() blocked by browser autoplay policy:",
            err?.name || err?.message || err,
            "— click anywhere in the page or interact with the WebPhone to unblock.",
          );
        });
      }
      return true;
    };

    if (tryMount()) return undefined;

    // Poll: 30 attempts × 100ms = 3s budget. Plenty of headroom for the
    // SDK's media negotiation while still bounded so we never leak.
    let attempts = 0;
    const handle = setInterval(() => {
      attempts += 1;
      if (tryMount() || attempts >= 30) {
        clearInterval(handle);
      }
    }, 100);
    return () => clearInterval(handle);
  }, [session, callState]);

  // ── Elapsed timer while connected ──
  useEffect(() => {
    if (callState === "connected") {
      callStartRef.current = Date.now();
      setElapsedSec(0);
      timerRef.current = setInterval(() => {
        setElapsedSec(Math.floor((Date.now() - callStartRef.current) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [callState]);

  const logSession = useCallback(async (s, outcome) => {
    try {
      // Iter 158 (Josh) — issue reports #2/#7/#8: when reps dial 2+ alt
      // numbers in a single LeadFocus visit, the session object's
      // SDK-derived `remoteNumber` can be stale/cached. Trust the
      // explicit `_dialedTo` we stamped in `placeCall` first, then
      // fall back to SDK fields, then to the prefilled number.
      const reliableTo = s?._dialedTo || s?.remoteNumber || s?.toNumber || prefillNumber || null;
      const reliableFrom = s?._dialedFrom || s?.localNumber || callerId || null;
      // Iter 179.20 (Ioannis #1db7e87b Feb 2026) — for OUTBOUND we
      // already know the lead (we dialed them), but for INBOUND we
      // were falling back to `activeLead.id` — i.e. whatever lead the
      // rep happened to be viewing. So when an unrelated number rang
      // in while the rep was on a different lead, the call got logged
      // against the wrong record. Resolve the lead by the CALLER's
      // phone number for inbound calls; only use `activeLead` as a
      // hint when the inbound match fails.
      const isInbound = (s?.direction === "inbound") || (s?.direction === "Inbound");
      let resolvedLeadId = s?._dialedLeadId || s?.leadId || null;
      if (isInbound && !resolvedLeadId) {
        // Try to match the caller's number against an existing lead.
        const callerNum = s?.remoteNumber || s?.fromNumber || reliableFrom || "";
        if (callerNum) {
          try {
            const lookup = await axios.get(
              `${API}/leads`,
              { params: { q: callerNum, limit: 1, fields: "slim" }, headers: authHeaders() },
            );
            const items = Array.isArray(lookup.data) ? lookup.data : (lookup.data.items || []);
            if (items.length > 0 && items[0]?.id) {
              resolvedLeadId = items[0].id;
            }
          } catch (_e) { /* match-by-phone failed — silent fallback */ }
        }
      }
      // Only fall back to activeLead for OUTBOUND, never for inbound.
      const reliableLeadId = resolvedLeadId || (isInbound ? null : (activeLead?.id || null));
      const res = await axios.post(`${API}/integrations/ringcentral/webphone/log`, {
        direction: s.direction || "outbound",
        from_number: reliableFrom,
        to_number: reliableTo,
        outcome,
        duration_sec: callStartRef.current ? Math.floor((Date.now() - callStartRef.current) / 1000) : 0,
        started_at: callStartRef.current ? new Date(callStartRef.current).toISOString() : new Date().toISOString(),
        external_id: s.callId,
        lead_id: reliableLeadId,
        // Sales Dialer context, only when the logged lead is the one dialed from the queue.
        campaign_id: (reliableLeadId && reliableLeadId === s?._dialedLeadId) ? (s._dialedCampaignId || null) : null,
        queue_item_id: (reliableLeadId && reliableLeadId === s?._dialedLeadId) ? (s._dialedQueueItemId || null) : null,
      }, { headers: authHeaders() });
      // Iter 140.5 — stash the activity_log row id on the session so
      // the override toast can PATCH the outcome if the rep flips it.
      if (s) s._loggedActivityId = res?.data?.id;
      // Iter 146 (Danny #4164a796) — fire a window-level event so the
      // currently-viewed LeadFocus page can auto-open the disposition
      // lightbox immediately after the call ends.
      const dur = callStartRef.current ? Math.floor((Date.now() - callStartRef.current) / 1000) : 0;
      window.dispatchEvent(new CustomEvent("webphone:call-ended", { detail: {
        lead_id: reliableLeadId,
        lead_name: activeLead?.name || null,
        activity_id: res?.data?.id || null,
        external_id: s.callId,
        outcome,
        duration_sec: dur,
        to_number: reliableTo,
      }}));
    } catch (e) {
      console.warn("webphone log failed", e);
    }
  }, [callerId, activeLead, prefillNumber]);

  // Iter 140.5 → Iter 210.7 (Feb 2026 · AriaOne "unify after-call-work"
  // decision) — DEPRECATED. The top-right VM-vs-Human override toast
  // was removed because it duplicated the full disposition modal that
  // opens seconds later. Function kept as a no-op stub for a session
  // to avoid ReferenceErrors from stale callers; safe to delete on the
  // next cleanup pass.
  const showOutcomeOverrideToast = useCallback((/* s, autoOutcome */) => {
    /* no-op — see Iter 210.7 */
  }, []);

  // ── Outbound ──
  const placeCall = useCallback(async (overrideNumber) => {
    if (!wpRef.current || regState !== "registered") {
      toast.error("Softphone not registered yet");
      return;
    }
    // Iter 192k (Joshua) added an optional `overrideNumber` so the auto-
    // dial path could bypass the closure race. But the in-dock Call
    // button is bound `onClick={placeCall}` — React then passes the
    // SyntheticEvent object as the first argument, which my unchecked
    // `?? dialNumber` treated as the number → either crash on .replace()
    // or empty `num` → silent no-op. Reproduced on Lead Focus: green
    // Call button in the softphone mini-window did nothing.
    //
    // Iter 192n — only honor overrideNumber when it's a string; for
    // anything else (Event, undefined, null, etc.) fall through to
    // `dialNumber`. This keeps the auto-dial fast path (`placeCall(num)`)
    // intact while restoring the click-handler path.
    const overrideStr = typeof overrideNumber === "string" ? overrideNumber : "";
    const sourceNum = overrideStr || dialNumber || "";
    const num = sourceNum.replace(/[^\d+*#]/g, "");
    if (!num) return;

    // Make sure mic is granted before invoking the SDK — otherwise the SDK
    // throws "Permission denied" with no context. If denied, show specific
    // guidance instead of the raw error.
    if (micState !== "granted") {
      const ok = await requestMic();
      if (!ok) {
        toast.error(
          "Microphone access is blocked. Click the 🔒 icon in your address bar, allow microphone, then try again. (If you're inside Emergent's preview iframe, open this app in a new tab.)",
          { duration: 10000 }
        );
        return;
      }
    }

    try {
      setCallState("dialing");
      const s = await wpRef.current.call(num, callerId || undefined);
      // Iter 158 (Josh) — issue reports #7/#8: tag the session with the
      // ACTUAL dialed number + from/lead so logSession doesn't read a
      // stale/reused `s.remoteNumber` from the SDK on subsequent calls.
      try {
        s._dialedTo = num;
        s._dialedFrom = callerId || null;
        s._dialedLeadId = activeLead?.id || null;
        s._dialedCampaignId = activeLead?.campaignId || null;
        s._dialedQueueItemId = activeLead?.queueItemId || null;
      } catch (_e) { /* defensive — some SDK proxies are frozen */ }
      setSession(s);
      lastSessionRef.current = s;
      sessionStartRef.current = Date.now();
      setCallerInfo({ number: num, name: "" });

      // Iter 140.5 — listen for MULTIPLE SDK events that indicate the
      // remote party accepted the call. Different RC WebPhone SDK
      // versions + SIP signaling paths surface different events; the
      // legacy code only handled "answered" which missed legitimate
      // connected calls (Stephanie issue 0394a122).
      const markAnswered = () => {
        // Iter 173 (Lisa bug 32cfae57) — RESET the start timestamp on
        // every answered event. Previously this was guarded with
        // `if (!callStartRef.current)`, which meant the dialer-queue
        // flow (call → disposed → next call → answered) kept reusing
        // the FIRST call's timestamp, producing identical `started_at`
        // and ever-growing `duration_sec` values on every subsequent
        // call. Always overwrite so each call gets its own correct
        // start time. The useEffect at line 188 still sets it on the
        // `connected` state transition, but answered events are the
        // earliest reliable signal so we anchor here.
        callStartRef.current = Date.now();
        setCallState("connected");
        // Iter 158 (Josh) — broadcast call-started so the floating
        // dock can update its collapsed-pill label with a live timer.
        try {
          window.dispatchEvent(new CustomEvent("webphone:call-started", { detail: {
            lead_id: activeLead?.id || null,
            to_number: num,
            started_at: callStartRef.current,
            // Iter 185 — expose the RC/SIP identifiers so the picker
            // can pass them to /calls/conference for the Cloud API
            // `bring-in` path. All three fields are safe to be null.
            call_id: (s && s.callId) || null,
            telephony_session_id: (s && s.sessionId) || null,
            party_id: (s && s.partyId) || null,
          }}));
        } catch (_e) { /* defensive */ }
      };
      // Iter 188.36 · Fix G — the `ringcentral-web-phone` v2.4.3 SDK
      // (see `dist/call-session/outbound.cjs:44-54`) ONLY emits the
      // `"answered"` event when the SIP server responds with EXACTLY
      // the subject `"SIP/2.0 200 OK"`. For carrier paths that route
      // through PBX / hunt groups / voicemail-with-early-media, the
      // final 200 OK sometimes never reaches the SDK cleanly — the
      // call still has audio flow (early-media path) but `answered`
      // never fires. That's the destinee@ehiehr.com timer-stall bug:
      // her on-screen timer stayed at 0:00 the whole call because the
      // useEffect at line 2097 (SalesDialerLive.jsx) only ticks when
      // local `state === "connected"`, which only happens after
      // `markAnswered` is called.
      //
      // Fix: also listen for `"ringing"` (which DOES fire reliably —
      // outbound.cjs:37 emits it as soon as the SIP INVITE is ACKed)
      // and arm a 3-second grace timer. If `answered` or `failed`
      // hasn't fired within 3 s of ringing, optimistically call
      // `markAnswered` so the timer starts ticking. Slight cost:
      // legitimate no-answer calls (which typically ring 15-25 s
      // before the carrier gives up) will show 3 s of on-screen
      // "connected" time before hanging up. Massively better than the
      // current 0:00 through the entire call.
      let ringingGraceTimer = null;
      const cancelRingingGrace = () => {
        if (ringingGraceTimer) {
          clearTimeout(ringingGraceTimer);
          ringingGraceTimer = null;
        }
      };
      s.on("ringing", () => {
        // SDK confirmed the SIP INVITE was ACKed — remote line is
        // ringing. Arm the grace timer. If `answered` / `failed` /
        // `disposed` beats it, they cancel the timer.
        cancelRingingGrace();
        ringingGraceTimer = setTimeout(() => {
          // markAnswered is idempotent (setState to same value = no-op).
          if (!callStartRef.current) markAnswered();
        }, 3000);
      });
      s.on("answered", () => {
        cancelRingingGrace();
        markAnswered();
      });
      // Iter 188.36 · Fix G cleanup — deleted `accepted` / `confirmed`
      // / `progress` subscriptions. Verified against the actual
      // compiled SDK (v2.4.3 `dist/call-session/outbound.cjs`): those
      // three events are NEVER emitted by this SDK version. They were
      // leftovers from an older SIP.js-based build and functioned as
      // dead code — silent listeners that never fired but gave a
      // false sense of coverage.
      s.once("failed", (msg) => {
        // Iter 188.36 · Fix G — cancel the ringing-grace timer so a
        // fail-within-3s doesn't optimistically flip us to "connected".
        cancelRingingGrace();
        // Iter 168.e (Josh) — Priyanka A "missing calls" bug.
        // The legacy handler returned early on `failed`, leaving 5-7
        // calls per rep per day completely UNLOGGED in CRM. RC's call
        // log showed them all; the CRM had nothing. Map common SIP
        // failure reasons to call outcomes and persist via the same
        // `/webphone/log` path. Guard against double-logging when
        // `disposed` also fires on the same session.
        if (!s._loggedAt) {
          s._loggedAt = Date.now();
          const reason = String(msg?.reasonPhrase || msg?.cause || "").toLowerCase();
          const statusCode = msg?.statusCode || msg?.status_code;
          // SIP failure → CRM outcome mapping
          let failureOutcome = "no-answer";
          if (statusCode === 486 || /busy/.test(reason)) {
            failureOutcome = "busy";
          } else if (statusCode === 487 || /cancel/.test(reason)) {
            // 487 = "Request Terminated" → rep cancelled before connect
            failureOutcome = "no-answer";
          } else if (statusCode === 480 || /unavailable|temporarily/.test(reason)) {
            failureOutcome = "no-answer";
          } else if (statusCode === 404 || /not.?found|invalid/.test(reason)) {
            failureOutcome = "invalid-number";
          } else if (statusCode === 408 || /timeout/.test(reason)) {
            failureOutcome = "no-answer";
          } else if (statusCode === 603 || /declined|reject/.test(reason)) {
            failureOutcome = "rejected";
          }
          logSession(s, failureOutcome);
        }
        setCallState("idle");
        setSession(null);
        // Iter 173 (Lisa bug 32cfae57) — clear per-call start timestamp
        // on failure too, so a busy/cancelled call doesn't leak its
        // baseline into the next dialer-queue call.
        callStartRef.current = null;
        sessionStartRef.current = null;
        const failureMsg = msg?.reasonPhrase || msg?.statusCode || "unknown";
        toast.error(`Call failed: ${failureMsg}`);
      });
      s.once("disposed", () => {
        // Iter 188.36 · Fix G — cancel the ringing-grace timer so a
        // disposed-within-3s (rare, but happens on immediate hangup)
        // doesn't leak an optimistic markAnswered() after the session
        // has already been torn down.
        cancelRingingGrace();
        // Guard against double-logging if `failed` already logged this
        // session (Priyanka A bug — both events fire on busy/cancelled
        // calls and we'd race-condition the activity_log row).
        if (s._loggedAt) {
          setSession(null);
          setCallState("idle");
          setMuted(false);
          setHeld(false);
          setShowDtmf(false);
          sessionStartRef.current = null;
          // Iter 174.5 (Danny B3 fix — Himagna inflated duration). Also
          // clear callStartRef on this early-return path. Previously,
          // when `failed` ran first and `disposed` short-circuited, the
          // per-call start timestamp could persist into the NEXT call,
          // producing durations that span hours instead of seconds.
          callStartRef.current = null;
          return;
        }
        s._loggedAt = Date.now();
        // Three-tier outcome detection (Iter 188.36 · Fix G — threshold
        // tightened from 12 s → 8 s because the new `ringing`-based
        // grace timer already catches the "SDK never fired answered"
        // path at 3 s. The disposed rescue is now only for the edge
        // case where BOTH ringing and grace-timer missed — e.g., the
        // SDK jumped straight from init → disposed without emitting
        // ringing at all):
        // 1. Ringing grace timer fired → callStartRef set → "connected"
        // 2. `answered` event fired → callStartRef set → "connected"
        // 3. No signal at all but session lasted >= 8 s → "connected"
        // 4. Else → "no-answer"
        const sessionDur = sessionStartRef.current
          ? Math.floor((Date.now() - sessionStartRef.current) / 1000)
          : 0;
        let outcome;
        if (callStartRef.current) outcome = "connected";
        else if (sessionDur >= 8) outcome = "connected";
        else outcome = "no-answer";
        // Fallback: if we inferred "connected" without an explicit
        // answered event, retroactively backfill callStartRef so the
        // duration sent to the backend reflects the session lifetime.
        if (outcome === "connected" && !callStartRef.current && sessionStartRef.current) {
          callStartRef.current = sessionStartRef.current;
        }
        logSession(s, outcome);
        // Iter 210.7 (Feb 2026 · AriaOne "unify after-call-work log")
        // — removed `showOutcomeOverrideToast(s, outcome)` here. The
        // top-right VM-vs-Human toast is redundant now that the full
        // disposition modal opens on both LeadFocus and SalesDialer
        // ~800ms after the call ends. The full modal covers the same
        // outcome-override + notes + stage + follow-up flow more
        // thoroughly, so the toast just created UI noise.
        setSession(null);
        setCallState("idle");
        setMuted(false);
        setHeld(false);
        setShowDtmf(false);
        sessionStartRef.current = null;
        // Iter 173 (Lisa bug 32cfae57) — also clear the per-call start
        // timestamp so the NEXT dialer-queue call doesn't inherit the
        // current call's start time (and report a cumulative duration).
        callStartRef.current = null;
      });
    } catch (e) {
      console.error("call() failed", e);
      const msg = String(e?.message || e);
      if (/permission denied|notallowed/i.test(msg)) {
        toast.error(
          "Microphone access is blocked. Click the 🔒 in your address bar → Allow microphone → reload the page.",
          { duration: 10000 }
        );
        setMicState("denied");
      } else {
        toast.error(`Couldn't place call: ${msg}`);
      }
      setCallState("idle");
    }
  }, [dialNumber, regState, callerId, logSession, micState, requestMic]);

  // ── Inbound actions ──
  const acceptInbound = useCallback(async () => {
    if (!inbound) return;
    try {
      const s = inbound;
      setSession(s);
      setCallState("connected");
      // Iter 168.e (Josh) — same fix as outbound: log on failed too.
      s.once("failed", (msg) => {
        if (!s._loggedAt) {
          s._loggedAt = Date.now();
          logSession(s, "no-answer");
        }
        setCallState("idle");
        setSession(null);
        toast.error(`Call dropped: ${msg?.reasonPhrase || msg?.statusCode || "unknown"}`);
      });
      s.once("disposed", () => {
        if (s._loggedAt) {
          setSession(null);
          setCallState("idle");
          setMuted(false);
          setHeld(false);
          return;
        }
        s._loggedAt = Date.now();
        logSession(s, "connected");
        setSession(null);
        setCallState("idle");
        setMuted(false);
        setHeld(false);
      });
      setInbound(null);
      await s.answer();
    } catch (e) {
      toast.error(`Couldn't answer: ${e.message || e}`);
    }
  }, [inbound, logSession]);

  const declineInbound = useCallback(async () => {
    if (!inbound) return;
    try { await inbound.decline(); } catch {}
    setInbound(null);
  }, [inbound]);

  // ── In-call controls ──
  const hangup = useCallback(async () => {
    if (!session) return;
    setCallState("ending");
    try { await session.hangup(); } catch (e) { console.warn("hangup err", e); }
  }, [session]);

  const toggleMute = useCallback(() => {
    if (!session) return;
    try {
      if (muted) { session.unmute(); setMuted(false); }
      else { session.mute(); setMuted(true); }
    } catch (e) { console.warn(e); }
  }, [session, muted]);

  const toggleHold = useCallback(async () => {
    if (!session) return;
    try {
      if (held) { await session.unhold(); setHeld(false); }
      else { await session.hold(); setHeld(true); }
    } catch (e) {
      console.warn(e);
      toast.error("Hold/unhold failed");
    }
  }, [session, held]);

  // ── DTMF buffering during pre-answer ─────────────────────────────────
  // RC WebPhone 2.x SDK limitation: `session.sendDtmf()` silently no-ops
  // while the call is still ringing/dialing (the SIP session isn't
  // established yet, so there's no RTP path for RFC4733 tones). Reps
  // dialing into a business IVR were pressing 1/4/etc during ringing
  // and nothing was being sent — discovered May 2026 by super-user
  // testing 330-284-4401.
  //
  // Fix: buffer tones pressed before connect, then flush them as soon
  // as callState flips to "connected". 150ms gap between tones so the
  // IVR has time to recognize each one.
  const pendingDtmfRef = useRef([]);
  const [pendingDtmfCount, setPendingDtmfCount] = useState(0);

  // ── Local DTMF audio feedback (iter 138 — tatiana@ehiehr.com bug fix) ─
  // Issue: users pressing the in-call dial pad while the IVR is still
  // ringing saw the "queued" banner and assumed the keypad was broken
  // because there was no audible feedback. The keypad WAS working — RC
  // SDK just buffers pre-answer DTMF — but with zero local tone it felt
  // dead. We now play the standard DTMF two-tone (RFC2833 frequency pair)
  // locally on EVERY press via Web Audio API. This is purely a UI cue and
  // never travels the SIP/RTP path; the real `session.sendDtmf` still
  // handles the over-the-wire transmission.
  const audioCtxRef = useRef(null);
  const DTMF_FREQ = {
    "1": [697, 1209], "2": [697, 1336], "3": [697, 1477],
    "4": [770, 1209], "5": [770, 1336], "6": [770, 1477],
    "7": [852, 1209], "8": [852, 1336], "9": [852, 1477],
    "*": [941, 1209], "0": [941, 1336], "#": [941, 1477],
  };
  const playLocalDtmf = useCallback((tone) => {
    try {
      const pair = DTMF_FREQ[tone];
      if (!pair) return;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      // Some browsers suspend the context until a user gesture; the dial pad
      // tap IS that gesture so resume is safe.
      if (ctx.state === "suspended") ctx.resume();
      const dur = 0.14;
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
      gain.connect(ctx.destination);
      pair.forEach((f) => {
        const o = ctx.createOscillator();
        o.type = "sine";
        o.frequency.value = f;
        o.connect(gain);
        o.start(now);
        o.stop(now + dur);
      });
    } catch (e) { /* audio feedback is non-critical */ void e; }
  }, []);

  const sendDtmf = useCallback((tone) => {
    if (!session) return;
    // Always give the rep immediate audible + visual feedback so the
    // keypad NEVER feels "dead" — even while the SIP path is buffering.
    playLocalDtmf(tone);
    setDialNumber((d) => d + tone);
    // Iter 139 (Tatiana May-25 ticket #59299023): try sending the DTMF
    // *immediately* via the RC SDK regardless of `callState`. Many
    // business IVRs answer the call with SIP 183 Session Progress +
    // early media (you HEAR the menu) but the RC `answered` event
    // (SIP 200 OK) doesn't fire until you press a digit — chicken-and-
    // egg. Worse: even when the SDK is in "dialing" state, SIP INFO
    // out-of-band DTMF works fine and is what RC actually uses under
    // the hood. So try the live send first; if it throws or there's
    // no RTP path yet, fall back to the buffer + auto-flush logic.
    let liveSent = false;
    try {
      if (typeof session.sendDtmf === "function") {
        session.sendDtmf(tone);
        liveSent = true;
      }
    } catch (e) {
      console.warn("[RCWebPhone] live sendDtmf failed, will buffer:", e);
    }
    if (!liveSent && callState !== "connected") {
      pendingDtmfRef.current.push(tone);
      setPendingDtmfCount(pendingDtmfRef.current.length);
    }
  }, [session, callState, playLocalDtmf]);

  // Flush buffered tones the instant the call transitions to connected.
  useEffect(() => {
    if (callState !== "connected" || !session) return;
    const buffered = pendingDtmfRef.current.splice(0);
    if (!buffered.length) return;
    setPendingDtmfCount(0);
    let i = 0;
    const send = () => {
      if (i >= buffered.length) return;
      try { session.sendDtmf(buffered[i]); } catch (e) { console.warn(e); }
      i += 1;
      setTimeout(send, 150);  // IVRs need ~80-120ms minimum between tones
    };
    send();
  }, [callState, session]);

  // Drop buffered tones whenever the call ends so they don't leak into
  // the next call.
  useEffect(() => {
    if (callState === "idle") {
      pendingDtmfRef.current = [];
      setPendingDtmfCount(0);
    }
  }, [callState]);

  // Bridge external dialer pages (SalesDialerLive) to this session via
  // window events. SalesDialerLive's CallStage buttons dispatch
  // `webphone:control` with { action, tone } so the in-page softphone
  // shell can drive mute / hold / DTMF / hangup on the active RC
  // session without us mounting another <RCWebPhone> instance.
  // (Super-user fix May 2026 — DTMF must work during IVR ring phase.)
  //
  // Iter 185 (Feb 2026) — Transfer + Conference support: 5 new actions
  // wire the picker's blind/warm-transfer and conference bring-in flows
  // to the RC WebPhone SDK's native session.transfer / warmTransfer.
  // Warm-transfer keeps the newSession handle in a ref so a later
  // `transfer-warm-complete` or `transfer-warm-cancel` finds it.
  const _warmXferRef = useRef(null);
  useEffect(() => {
    const onControl = async (ev) => {
      const action = ev?.detail?.action;
      if (!action) return;
      if (action === "mute") return toggleMute();
      if (action === "hold") return toggleHold();
      if (action === "hangup") return hangup();
      if (action === "dtmf") {
        const tone = String(ev?.detail?.tone || "");
        if (tone) sendDtmf(tone);
        return;
      }
      // ── Iter 185: Transfer / Conference ─────────────────────────────
      if (!session) return;
      const target = String(ev?.detail?.target || "").trim();
      try {
        if (action === "transfer-blind") {
          if (!target) return;
          await session.transfer(target);
          window.dispatchEvent(new CustomEvent("webphone:transfer-state", { detail: { state: "completed", mode: "blind", target } }));
        } else if (action === "transfer-warm-start") {
          if (!target) return;
          const handles = await session.warmTransfer(target);
          _warmXferRef.current = handles;
          window.dispatchEvent(new CustomEvent("webphone:transfer-state", { detail: { state: "target-ringing", mode: "warm", target } }));
        } else if (action === "transfer-warm-complete") {
          const h = _warmXferRef.current;
          if (h?.complete) {
            await h.complete();
            _warmXferRef.current = null;
            window.dispatchEvent(new CustomEvent("webphone:transfer-state", { detail: { state: "completed", mode: "warm" } }));
          }
        } else if (action === "transfer-warm-cancel") {
          const h = _warmXferRef.current;
          if (h?.cancel) {
            await h.cancel();
            _warmXferRef.current = null;
            window.dispatchEvent(new CustomEvent("webphone:transfer-state", { detail: { state: "cancelled", mode: "warm" } }));
          }
        } else if (action === "conference-add") {
          // Cloud API path is handled by the backend response the picker
          // received. If backend returned mode=fallback, we perform a
          // warm-transfer to the target (the merge step is manual today
          // — the rep clicks "Complete" once the target answers).
          const fallback = ev?.detail?.fallback === true;
          if (fallback && target) {
            const handles = await session.warmTransfer(target);
            _warmXferRef.current = handles;
            window.dispatchEvent(new CustomEvent("webphone:transfer-state", {
              detail: { state: "target-ringing", mode: "warm", target, conference: true },
            }));
          }
          // If mode=cloud, RC server-side already brought the party in;
          // no SDK action needed — the UI simply shows the new party.
        }
      } catch (err) {
        console.warn("[webphone.control]", action, err?.message || err);
        window.dispatchEvent(new CustomEvent("webphone:transfer-state", {
          detail: { state: "error", action, error: String(err?.message || err) },
        }));
      }
    };
    window.addEventListener("webphone:control", onControl);
    return () => window.removeEventListener("webphone:control", onControl);
  }, [toggleMute, toggleHold, hangup, sendDtmf, session]);

  // Iter 192e (Priyanka June 23 video — "connecting but no sound / no
  // dialpad"): when any page (LeadProfileSlideIn, SalesDialerLive,
  // LeadFocus quick-call) dispatches `webphone:dial`, we previously
  // only prefilled the number — the rep then had to find the dock and
  // click Call manually. Now we ALSO auto-place the call so the rep's
  // intent ("click Call → call goes out") matches reality. We only
  // auto-place when there's no active session and the SIP layer is
  // registered, so we never disturb an in-flight call.
  useEffect(() => {
    const onAutoDial = (ev) => {
      const num = String(ev?.detail?.number || "").trim();
      if (!num) return;
      // Iter 192j (Ioannis June 24 video — "Sometimes the green button
      // dials, sometimes it just puts the number in the dialer"). The
      // previous guard checked `if (session) return;` to avoid disturbing
      // an in-flight call. But after a rep hangs up, the `session`
      // reference can linger for a few hundred ms before our hangup
      // handler runs setSession(null) — if the rep clicks Call on the
      // next lead during that window, we silently bailed out (prefill
      // only, no dial). Now we ALSO consult callState so we only block
      // when there's a TRULY active call (dialing / ringing / connected).
      // If callState is idle / ending / ended we proceed even when the
      // stale session reference is still around.
      const ACTIVE_STATES = new Set(["dialing", "ringing", "connected", "held"]);
      if (session && ACTIVE_STATES.has(callState)) {
        return;
      }
      // Defer one tick so the prefillNumber prop has time to flow
      // through state and `placeCall` reads the right number.
      setDialNumber(num);
      setTimeout(() => {
        if (!wpRef.current || regState !== "registered") {
          if (regState === "failed") {
            toast.error("Softphone failed to register. Reconnect RingCentral in Settings.");
          } else if (regState !== "registered") {
            toast.info("Softphone is still connecting — try again in a few seconds.");
          }
          return;
        }
        // Iter 192j — if a stale session is still around, tear it down
        // first so placeCall doesn't no-op. Idempotent: hangup on an
        // already-ended session just clears local state.
        if (session && !ACTIVE_STATES.has(callState)) {
          try { hangup(); } catch { /* defensive */ }
        }
        // Iter 192k — pass the number explicitly so we don't rely on the
        // setDialNumber call having committed to React state yet.
        placeCall(num);
      }, 120);
    };
    window.addEventListener("webphone:dial", onAutoDial);
    return () => window.removeEventListener("webphone:dial", onAutoDial);
  }, [session, callState, regState, placeCall, hangup]);

  // Broadcast call-state changes so external dialer shells can mirror them.
  useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent("webphone:state", {
        detail: { state: callState, muted, held, hasSession: !!session },
      }));
    } catch { /* CustomEvent unavailable */ }
  }, [callState, muted, held, session]);

  const isInCall = !!session && (callState === "dialing" || callState === "connected" || callState === "ending");
  // Iter 193 (Tatiana #a85a8d0d) — keep the ref in sync for the
  // inboundCall handler (mounted once, in init()).
  useEffect(() => { isInCallRef.current = isInCall; }, [isInCall]);
  const statusColor = {
    registered: "var(--success, #10b981)",
    registering: "var(--blue, #5B6CFF)",
    provisioning: "var(--blue, #5B6CFF)",
    disconnected: "var(--text-4, #94a3b8)",
    failed: "var(--danger, #ef4444)",
  }[regState];

  return (
    <div data-testid="rc-webphone" style={{
      border: "0.5px solid var(--border, #e2e8f0)", borderRadius: 14,
      background: "#fff", padding: 18, maxWidth: 380, fontFamily: "inherit",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    }}>
      {/* Header: registration status */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {regState === "registered" ? <Wifi style={{ width: 14, height: 14, color: statusColor }} />
            : regState === "failed" ? <WifiOff style={{ width: 14, height: 14, color: statusColor }} />
            : <Loader2 style={{ width: 14, height: 14, color: statusColor, animation: "spin 1s linear infinite" }} />}
          <span data-testid="webphone-status" style={{ fontSize: 12, fontWeight: 600, color: statusColor, textTransform: "capitalize" }}>
            {regState}
          </span>
        </div>
        {callerId && (
          <span style={{ fontSize: 11, color: "var(--text-4)" }} title="Outbound caller-ID">
            from {callerId}
          </span>
        )}
      </div>

      {regError && (
        <div data-testid="webphone-error" style={{
          fontSize: 12, color: "var(--danger, #ef4444)",
          background: "#fef2f2", border: "0.5px solid #fecaca",
          padding: 8, borderRadius: 8, marginBottom: 12,
          display: "flex", alignItems: "flex-start", gap: 6,
        }}>
          <AlertCircle style={{ width: 13, height: 13, marginTop: 1, flexShrink: 0 }} />
          <span>{regError}</span>
        </div>
      )}

      {/* OAuth-required state: rep hasn't connected their personal RC yet.
          Render a soft inline CTA instead of the red error banner — this is
          an expected state per the Bundle 2 fallback policy (block, not JWT). */}
      {oauthRequired && (
        <div data-testid="webphone-oauth-required" style={{
          fontSize: 12, color: "#92400e",
          background: "#fffbeb", border: "0.5px solid #fcd34d",
          padding: 12, borderRadius: 8, marginBottom: 12,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
            <AlertCircle style={{ width: 13, height: 13 }} />
            Connect your RingCentral
          </div>
          <div style={{ lineHeight: 1.45, marginBottom: 8 }}>
            Each rep dials from their own caller-ID. Click below to link your
            personal RingCentral account — takes ~30 seconds.
          </div>
          <a
            href="/settings?section=integrations"
            data-testid="webphone-connect-settings-link"
            style={{
              display: "inline-block", padding: "6px 12px", background: "#2563eb",
              color: "#fff", borderRadius: 6, textDecoration: "none",
              fontSize: 11, fontWeight: 600,
            }}
          >
            Open Settings → Connect
          </a>
        </div>
      )}

      {/* Mic permission banner — surfaces the most common cause of "Permission
         denied" errors before the user clicks Call. */}
      {(micState === "denied" || micState === "unsupported") && (
        <div data-testid="mic-permission-banner" style={{
          fontSize: 12, color: "#92400e",
          background: "#fffbeb", border: "0.5px solid #fcd34d",
          padding: 10, borderRadius: 8, marginBottom: 12,
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 8 }}>
            <MicOff style={{ width: 13, height: 13, marginTop: 1, flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>
                {micState === "unsupported" ? "Browser doesn't support microphone access" : "Microphone access is blocked"}
              </div>
              <div style={{ lineHeight: 1.45 }}>
                {micState === "unsupported"
                  ? "Try Chrome, Edge, or Firefox over HTTPS."
                  : "Click the 🔒 icon in your address bar → Allow microphone → reload. If you're using the Emergent preview, open the app in a new tab — iframes block mic by default."}
              </div>
            </div>
          </div>
          {micState === "denied" && (
            <button
              data-testid="retry-mic-btn"
              onClick={requestMic}
              style={{
                padding: "5px 10px", fontSize: 11, fontWeight: 600,
                background: "#fff", color: "#92400e",
                border: "0.5px solid #fcd34d", borderRadius: 6, cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Retry microphone access
            </button>
          )}
        </div>
      )}

      {/* Inbound call popup */}
      {inbound && (
        <div data-testid="inbound-call-popup" style={{
          background: "linear-gradient(135deg, #5B6CFF 0%, #4051d8 100%)",
          color: "#fff", padding: 16, borderRadius: 12, marginBottom: 14,
          animation: "pulse 1.5s ease-in-out infinite",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <PhoneIncoming style={{ width: 16, height: 16 }} />
            <span style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Incoming call
            </span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
            {callerInfo.name || callerInfo.number || "Unknown caller"}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button data-testid="answer-call-btn" onClick={acceptInbound} style={{
              flex: 1, padding: "10px 14px", background: "#10b981", color: "#fff",
              border: 0, borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <Phone style={{ width: 13, height: 13 }} /> Answer
            </button>
            <button data-testid="decline-call-btn" onClick={declineInbound} style={{
              flex: 1, padding: "10px 14px", background: "rgba(255,255,255,0.18)", color: "#fff",
              border: 0, borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <X style={{ width: 13, height: 13 }} /> Decline
            </button>
          </div>
        </div>
      )}

      {/* Active call panel */}
      {isInCall && (
        <div data-testid="active-call-panel" style={{
          background: "var(--surface-2, #f8fafc)", border: "0.5px solid var(--border)",
          borderRadius: 10, padding: 14, marginBottom: 14,
        }}>
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: "var(--text-4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
              {callState === "dialing" ? "Calling…" : callState === "connected" ? "Connected" : "Ending…"}
            </div>
            <div style={{ fontSize: 20, fontWeight: 600, color: "var(--navy)" }} data-testid="active-call-number">
              {callerInfo.number || "—"}
            </div>
            {callState === "connected" && (
              <div data-testid="call-timer" style={{ fontSize: 13, color: "var(--text-3)", marginTop: 4 }}>
                {fmtDuration(elapsedSec)}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 8 }}>
            {/* In-call controls are now enabled the moment a session exists
                (dialing / early-media / connected) so reps can: mute before
                the callee answers; press # / * to navigate auto-attendants
                that play DURING the ring phase; and put a call on hold. Only
                "ending" disables them. */}
            <button data-testid="mute-btn" onClick={toggleMute} disabled={!session || callState === "ending"} style={{
              padding: 10, background: muted ? "var(--danger, #ef4444)" : "#fff",
              color: muted ? "#fff" : "var(--navy)",
              border: `0.5px solid ${muted ? "var(--danger)" : "var(--border)"}`,
              borderRadius: 999, cursor: (!session || callState === "ending") ? "not-allowed" : "pointer",
              opacity: (!session || callState === "ending") ? 0.5 : 1,
            }} title={muted ? "Unmute" : "Mute"}>
              {muted ? <MicOff style={{ width: 16, height: 16 }} /> : <Mic style={{ width: 16, height: 16 }} />}
            </button>
            <button data-testid="hold-btn" onClick={toggleHold} disabled={!session || callState !== "connected"} style={{
              padding: 10, background: held ? "var(--blue)" : "#fff",
              color: held ? "#fff" : "var(--navy)",
              border: `0.5px solid ${held ? "var(--blue)" : "var(--border)"}`,
              borderRadius: 999,
              // Use `default` cursor (not `not-allowed`) so we don't show
              // the browser's red-slash circle icon — super-user May 2026
              // feedback was that the red-slash looked like a malfunction
              // when really Hold is just unavailable until the call is
              // actually answered (RC SDK limitation).
              cursor: (!session || callState !== "connected") ? "default" : "pointer",
              opacity: (!session || callState !== "connected") ? 0.35 : 1,
              transition: "opacity 0.15s",
            }} title={held ? "Resume" : callState !== "connected" ? "Hold becomes available once the other side answers" : "Hold"}>
              {held ? <Play style={{ width: 16, height: 16 }} /> : <Pause style={{ width: 16, height: 16 }} />}
            </button>
            <button data-testid="dtmf-toggle-btn" onClick={() => setShowDtmf((v) => !v)} disabled={!session || callState === "ending"} style={{
              padding: 10, background: showDtmf ? "var(--navy)" : "#fff",
              color: showDtmf ? "#fff" : "var(--navy)",
              border: `0.5px solid ${showDtmf ? "var(--navy)" : "var(--border)"}`,
              borderRadius: 999, cursor: (!session || callState === "ending") ? "not-allowed" : "pointer",
              opacity: (!session || callState === "ending") ? 0.5 : 1,
            }} title="Keypad — # / * for IVR navigation works during dialing too">
              <Hash style={{ width: 16, height: 16 }} />
            </button>
            <button data-testid="hangup-btn" onClick={hangup} style={{
              padding: 10, background: "var(--danger, #ef4444)", color: "#fff",
              border: 0, borderRadius: 999, cursor: "pointer",
            }} title="Hang up">
              <PhoneOff style={{ width: 16, height: 16 }} />
            </button>
          </div>

          {showDtmf && (
            <>
              {callState !== "connected" && (
                <div
                  data-testid="dtmf-queued-banner"
                  style={{
                    marginTop: 10, padding: "6px 10px", borderRadius: 6,
                    background: "rgba(48,209,88,0.10)", border: "0.5px solid rgba(48,209,88,0.35)",
                    color: "#1a7f3c", fontSize: 11, lineHeight: 1.4,
                  }}
                >
                  {pendingDtmfCount > 0
                    ? `${pendingDtmfCount} tone${pendingDtmfCount === 1 ? "" : "s"} queued · will auto-send the instant the call connects.`
                    : "Keypad live — tones send to the IVR immediately. If the menu doesn't react, it just hasn't answered yet."}
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginTop: 10 }}>
                {DIALPAD.map(([d]) => (
                  <button
                    key={`dtmf-${d}`}
                    data-testid={`dtmf-${d}`}
                    onClick={() => sendDtmf(d)}
                    style={{
                      padding: 10, background: "#fff",
                      border: "0.5px solid var(--border)",
                      borderRadius: 8, fontWeight: 600, cursor: "pointer",
                      color: "var(--navy)",
                    }}
                    title={`Send ${d}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Dial pad (idle) */}
      {!isInCall && (
        <>
          <input
            data-testid="dial-input"
            value={dialNumber}
            onChange={(e) => setDialNumber(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && placeCall()}
            placeholder="+1 555 123 4567"
            disabled={regState !== "registered"}
            style={{
              width: "100%", padding: "12px 14px", fontSize: 18,
              fontWeight: 500, color: "var(--navy)", textAlign: "center",
              border: "0.5px solid var(--border)", borderRadius: 10,
              marginBottom: 12, fontFamily: "inherit",
            }}
          />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 12 }}>
            {DIALPAD.map(([d, letters]) => (
              <button key={`pad-${d}`} data-testid={`pad-${d}`} onClick={() => setDialNumber((s) => s + d)} style={{
                padding: "12px 0", background: "#fff", border: "0.5px solid var(--border)",
                borderRadius: 8, cursor: "pointer", display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", color: "var(--navy)",
              }}>
                <span style={{ fontSize: 18, fontWeight: 600 }}>{d}</span>
                {letters && <span style={{ fontSize: 9, color: "var(--text-4)", letterSpacing: "0.05em" }}>{letters}</span>}
              </button>
            ))}
          </div>
          <button
            data-testid="place-call-btn"
            onClick={placeCall}
            disabled={!dialNumber || regState !== "registered"}
            style={{
              width: "100%", padding: "12px 14px",
              background: regState === "registered" && dialNumber ? "#10b981" : "var(--text-4)",
              color: "#fff", border: 0, borderRadius: 10, fontSize: 14, fontWeight: 600,
              cursor: dialNumber && regState === "registered" ? "pointer" : "not-allowed",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
              fontFamily: "inherit",
            }}>
            <Phone style={{ width: 14, height: 14 }} />
            Call
          </button>
        </>
      )}

      {regState === "registered" && !isInCall && !inbound && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12, fontSize: 11, color: "var(--text-4)", justifyContent: "center" }}>
          <CheckCircle2 style={{ width: 11, height: 11, color: "var(--success, #10b981)" }} />
          Ready · audio in browser
        </div>
      )}

      {/* Hidden audio mount (SDK injects <audio> elements here) */}
      <div ref={audioMountRef} style={{ display: "none" }} aria-hidden="true" />

      <style>{`
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(91,108,255,0.4); }
          50% { box-shadow: 0 0 0 8px rgba(91,108,255,0); }
        }
      `}</style>
    </div>
  );
}
